import { IPersonaProps, ValidationState, NormalPeoplePicker, Label } from "@fluentui/react";
import React from "react";
import { useLazyGetUsersQuery } from "../../redux/api/graph/api-graph-slice";
import { IGraphGetUsersRequest, IGraphGetUsersResponse } from "../../redux/api/graph/api-graph-interfaces";
import authModule from "../../../../../../utilities/AuthModule";
import { logger } from "../../../../../../utilities/logger";
import { validateEmail } from "../../../helpers/validateEmail";
import { IPerson } from "../../../../interfaces/IPersonResult";
import { searchPeople } from "../../../../services/GraphService";

export interface ICustomPersonaProps extends IPersonaProps {
  isCustomEmail?: boolean;
  isGuest?: boolean;
}

export interface IPeopleFinderProps {
  label: string;
  disabled?: boolean;
  allowExternalUsers?: boolean;
  itemLimit?: number;
  selectedPersonasChanged: (selectedPersonas: ICustomPersonaProps[], customTextValue?: string) => void;
}

export const PeopleFinder = ({ label, disabled, allowExternalUsers, itemLimit, selectedPersonasChanged }: IPeopleFinderProps) => {
  const [mostRecentlyUsed, setMostRecentlyUsed] = React.useState<IPersonaProps[]>([]);
  const selectedPersonas = React.useRef<IPersonaProps[]>([]);

  const [getUsers] = useLazyGetUsersQuery();

  const searchUsers = async (filterText: string): Promise<IGraphGetUsersResponse[]> => {
    const request: IGraphGetUsersRequest = {
      filter: filterText,
      orderby: 'displayName',
      select: 'id,displayName,mail,userPrincipalName,userType'
    }

    let result: any;
    try {
      result = await getUsers({ request, accessToken: await authModule.getGraphAccessToken() }).unwrap();
    } catch (error) {
      logger(error, "PeopleFinder.tsx (searchUsers)");
    }

    return result?.value ?? [];
  }

  const onFilterChanged = async (filterText: string) => {
    if (filterText) {
      let people: IPerson[];
      let users: IGraphGetUsersResponse[];

      [people, users] = await Promise.all([
        allowExternalUsers ? searchPeople(filterText, true, true) : [],
        searchUsers(filterText)
      ]);

      let personas: ICustomPersonaProps[] =
        mapUsers(users ?? [])
          .concat(mapPeople(people ?? []));

      if ((!Array.isArray(personas) || personas.length === 0) && validateEmail(filterText)) {
        personas = [{
          text: filterText,
          secondaryText: filterText,
          isGuest: true,
          isCustomEmail: true
        }];
      }

      let filteredPersonas: IPersonaProps[] = removeDuplicates(personas, selectedPersonas.current);
      filteredPersonas = sortResults(filterText, filteredPersonas).filter((persona, i, arr) => i === arr.findIndex(p => p.text === persona.text && p.secondaryText === persona.secondaryText));

      setMostRecentlyUsed(filteredPersonas);

      return filteredPersonas;
    }

    return [];
  };

  const onSelectedPersonasChange = (items: IPersonaProps[]) => {
    //item added
    if (items?.length > selectedPersonas.current?.length) {
      selectedPersonas.current = items;
      setMostRecentlyUsed(removeDuplicates(mostRecentlyUsed, selectedPersonas.current));
    }
    //item removed
    else if (items?.length < selectedPersonas.current?.length) {
      const itemRemoved = selectedPersonas.current.find(x => !items.includes(x));

      const newMostRecentlyList: IPersonaProps[] = [...mostRecentlyUsed];
      newMostRecentlyList.push(itemRemoved);

      selectedPersonas.current = items;

      setMostRecentlyUsed(removeDuplicates(newMostRecentlyList.sort((a, b) => (a.secondaryText > b.secondaryText) ? 1 : ((b.secondaryText > a.secondaryText) ? -1 : 0)), selectedPersonas.current));
    }

    selectedPersonasChanged(items);
  }

  return <div>
    <Label required>{label}</Label>
    <NormalPeoplePicker
      disabled={disabled}
      onChange={onSelectedPersonasChange}
      onResolveSuggestions={onFilterChanged}
      className={'ms-PeoplePicker'}
      onValidateInput={validateInput}
      removeButtonAriaLabel={'Remove'}
      onInputChange={onInputChange}
      itemLimit={Number.isInteger(itemLimit) && itemLimit > 0 ? itemLimit : undefined}
      resolveDelay={300}
    />
  </div>;
};

const mapUsers = (graphUsers: IGraphGetUsersResponse[]): ICustomPersonaProps[] => {
  return graphUsers
    .map(user => {
      const validatedEmailAddress: string = user.mail ?? (validateEmail(user.userPrincipalName) ? user.userPrincipalName : undefined);
      return validatedEmailAddress
        ? {
          text: user.displayName,
          secondaryText: validatedEmailAddress,
          isGuest: user.userType === "Guest"
        }
        : null;
    })
    .filter(user => user);
};

const mapPeople = (people: IPerson[]): ICustomPersonaProps[] => {
  return people.map(person => {
    let secondaryText: string;
    if (validateEmail(person.userPrincipalName)) {
      secondaryText = person.userPrincipalName;
    }
    else if (person.scoredEmailAddresses?.length > 0 && validateEmail(person.scoredEmailAddresses[0]?.address)) {
      secondaryText = person.scoredEmailAddresses[0].address;
    }

    return secondaryText
      ? { text: person.displayName, secondaryText, isGuest: person.personType.subclass === "Guest" }
      : null;
  })
    .filter(person => person);
};

const sortResults = (input: string, data: ICustomPersonaProps[]): ICustomPersonaProps[] => {
  try {
    let first: ICustomPersonaProps[] = [];
    let others: ICustomPersonaProps[] = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i].text.indexOf(input) == 0 || data[i].secondaryText.indexOf(input) == 0) {
        first.push(data[i]);
      } else {
        others.push(data[i]);
      }
    }
    first.sort((a, b) => a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1);
    others.sort((a, b) => a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1);
    return (first.concat(others).sort((a, b) => Number(a.isGuest) - Number(b.isGuest)));
  } catch (error) {
    console.warn(error);
  }
  return data;
}

function removeDuplicates(personas: IPersonaProps[], possibleDupes: IPersonaProps[]) {
  return personas.filter(persona => !listContainsPersona(persona, possibleDupes));
}

function listContainsPersona(persona: IPersonaProps, personas: IPersonaProps[]) {
  if (!personas || !personas.length || personas.length === 0) {
    return false;
  }
  return personas.some(item => item.text?.toLowerCase() === persona.text?.toLowerCase() && item.secondaryText?.toLowerCase() === persona.secondaryText?.toLowerCase());
}

function validateInput(input: string): ValidationState {
  if (input.indexOf('@') !== -1) {
    return ValidationState.valid;
  } else if (input.length > 1) {
    return ValidationState.warning;
  } else {
    return ValidationState.invalid;
  }
}

/**
 * Takes in the picker input and modifies it in whichever way
 * the caller wants, i.e. parsing entries copied from Outlook (sample
 * input: "Aaron Reid <aaron>").
 *
 * @param input The text entered into the picker.
 */
function onInputChange(input: string): string {
  const outlookRegEx = /<.*>/g;
  const emailAddress = outlookRegEx.exec(input);

  if (emailAddress && emailAddress[0]) {
    return emailAddress[0].substring(1, emailAddress[0].length - 1);
  }

  return input;
}